import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
import Switches from "vue-switches";
import { Guid } from "guid-typescript";
import BaseMixer from "@/router/views/BaseMixer";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import { toaster } from "@/utilities/toaster";
import { checkImageFile } from "@/utilities/imageFileCheck";
import { ClientContactModel } from "@/store/models/client/clientContact";
import { AddressModel } from "@/store/models/shared/address";
import ClientModule from "@/store/modules/client.module";
import { isIndia } from "@/types/validators";
setInteractionMode("passive");
/**
 * Edit Client Contact — inline form rendered inside ClientContactsTabComponent
 * while the shared ClientOverviewMachine is in loadedClientContactsInfo.contactAndTeamInfo.updateContactInfo.
 * Works on its own copy of the `contact` prop.
 */
let EditClientContactComponent = /** @class */ (() => {
    var _a, _b;
    let EditClientContactComponent = class EditClientContactComponent extends Mixins(BaseMixer, UnsavedChangesMixin) {
        constructor() {
            super(...arguments);
            this.editContactDTO = new ClientContactModel();
            this.originalIsKeyContact = false;
            this.previewImage = null;
            // The picked photo stays local (preview only) until Update uploads it
            this.pendingPhoto = null;
            this.uploadedPhotoFile = null;
            this.uploadedPhotoId = "";
            this.uploadingPhoto = false;
        }
        updateContact() {
            if (this.uploadingPhoto) {
                return;
            }
            this.validate("editClientContactObserver").then((res) => {
                if (res) {
                    // Same rule as deleting the Primary Contact (its Primary flag must be turned off first)
                    if (this.editContactDTO.isKeyContact && !this.editContactDTO.isActive) {
                        toaster.ShowError(this.originalIsKeyContact ? "The Primary Contact cannot be made inactive. Turn off Primary Contact for this contact first." : "An inactive contact cannot be the Primary Contact.");
                        return;
                    }
                    this.uploadPendingPhoto().then((uploaded) => {
                        if (!uploaded) {
                            return;
                        }
                        if (this.pendingPhoto) {
                            this.editContactDTO.mediaFileId = this.uploadedPhotoId;
                        }
                        this.machineService.send({
                            type: "UPDATING_CONTACT",
                            payLoad: this.editContactDTO,
                            keyContactChange: this.editContactDTO.isKeyContact !== this.originalIsKeyContact ? this.editContactDTO.isKeyContact : undefined,
                        });
                    });
                }
            });
        }
        // Uploads the picked photo (if any) and keeps its id; false when the upload failed and the contact must not be saved.
        // A photo already uploaded by an earlier, failed update isn't uploaded again.
        uploadPendingPhoto() {
            if (!this.pendingPhoto || (this.pendingPhoto === this.uploadedPhotoFile && this.uploadedPhotoId)) {
                return Promise.resolve(true);
            }
            const photo = this.pendingPhoto;
            const formData = new FormData();
            formData.append("file", photo);
            this.uploadingPhoto = true;
            return ClientModule.UploadClientPicture({ file: formData, type: "contacts" })
                .then((res) => {
                if (res) {
                    this.uploadedPhotoFile = photo;
                    this.uploadedPhotoId = res.mediaFileId;
                    return true;
                }
                toaster.ShowError("Client Contact image upload failed.");
                return false;
            }, () => false)
                .finally(() => {
                this.uploadingPhoto = false;
            });
        }
        cancelEditContact() {
            this.confirmLeave().then((leave) => {
                if (leave) {
                    this.machineService.send({ type: "CLOSE_MODAL" });
                }
            });
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin)
        unsavedState() {
            return {
                contact: this.editContactDTO,
                photo: this.pendingPhoto ? `${this.pendingPhoto.name}:${this.pendingPhoto.size}` : null,
            };
        }
        // Checks the file's content and keeps it for Update; nothing is uploaded yet
        fileSelected(e) {
            const input = e.target;
            const newFiles = input.files || e.dataTransfer.files;
            if (!newFiles.length)
                return;
            const file = newFiles[0];
            checkImageFile(file).then((error) => {
                if (error) {
                    // Clear the input so picking the same file again re-runs the check
                    input.value = "";
                    toaster.ShowError(error);
                    return;
                }
                this.pendingPhoto = file;
                const reader = new FileReader();
                reader.onload = () => {
                    this.previewImage = reader.result;
                };
                reader.readAsDataURL(file);
            });
        }
        // Computed
        get countryLists() {
            return ClientModule.countryLists;
        }
        get selectedCountryName() {
            const countryId = this.editContactDTO.contactAddress ? this.editContactDTO.contactAddress.countryId : null;
            const country = this.countryLists.find((x) => x.id.toString() === String(countryId));
            return country ? country.value : "";
        }
        get zipLabel() {
            return isIndia(this.selectedCountryName) ? "PIN Code" : "ZIP Code";
        }
        get stateLists() {
            if (this.editContactDTO.contactAddress != null && this.editContactDTO.contactAddress.countryId != null) {
                return ClientModule.stateLists.filter((x) => x.countryId == this.editContactDTO.contactAddress.countryId);
            }
            return [];
        }
        //Lifecycle
        created() {
            ClientModule.ClearMediaFileData();
            this.editContactDTO = Object.assign({}, this.contact);
            this.editContactDTO.contactAddress = this.contact.contactAddress ? Object.assign({}, this.contact.contactAddress) : new AddressModel();
            this.originalIsKeyContact = this.contact.isKeyContact;
        }
    };
    __decorate([
        Prop({ type: Object }),
        __metadata("design:type", typeof (_a = typeof ClientContactModel !== "undefined" && ClientContactModel) === "function" ? _a : Object)
    ], EditClientContactComponent.prototype, "contact", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", typeof (_b = typeof Guid !== "undefined" && Guid) === "function" ? _b : Object)
    ], EditClientContactComponent.prototype, "clientId", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], EditClientContactComponent.prototype, "machineService", void 0);
    EditClientContactComponent = __decorate([
        Component({
            name: "EditClientContactComponent",
            components: {
                Switches,
            },
        })
    ], EditClientContactComponent);
    return EditClientContactComponent;
})();
export default EditClientContactComponent;
